import { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Bell, CheckCheck, X, FileText, Megaphone, CreditCard, LifeBuoy, ListChecks, Target,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { Notification, NotificationType } from '@pms/shared';
import { notificationsService } from '@/services/notifications.service';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { formatRelativeTime } from '@/lib/dateFormat';
import {
  claimNotificationChime,
  isNotificationSoundEnabled,
  playNotificationSound,
  registerNotificationSoundUnlock,
} from '@/lib/notification-sound';
import { cn } from '@/lib/utils';

const CATEGORY_STYLES: Record<string, { icon: LucideIcon; className: string }> = {
  sop: { icon: FileText, className: 'bg-sky-500/10 text-sky-600 dark:text-sky-400' },
  notice: { icon: Megaphone, className: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' },
  subscription: { icon: CreditCard, className: 'bg-violet-500/10 text-violet-600 dark:text-violet-400' },
  ticket: { icon: LifeBuoy, className: 'bg-rose-500/10 text-rose-600 dark:text-rose-400' },
  task: { icon: ListChecks, className: 'bg-orange-500/10 text-orange-600 dark:text-orange-400' },
  goal: { icon: Target, className: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' },
};

// Also the worst-case delay before the new-notification sound plays.
const UNREAD_POLL_MS = 20_000;

const FALLBACK_STYLE = { icon: Bell, className: 'bg-primary/10 text-primary' };

function styleForType(type: NotificationType) {
  const category = type.split('_')[0] ?? '';
  return CATEGORY_STYLES[category] ?? FALLBACK_STYLE;
}

export function NotificationBell() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const unreadQuery = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => notificationsService.unreadSummary(),
    refetchInterval: UNREAD_POLL_MS,
  });

  const listQuery = useQuery({
    queryKey: ['notifications', { page: 1, limit: 10 }],
    queryFn: () => notificationsService.list({ page: 1, limit: 10 }),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['notifications'] });
  };

  useEffect(() => {
    registerNotificationSoundUnlock();
  }, []);

  // Chime when the newest unread notification gets newer. Dismiss / mark-read
  // only move it older or to null, so they never chime. The first value after
  // mount is only a baseline — no sound for what was already waiting.
  const latestAt = unreadQuery.data?.latest_at;
  const baselineRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (latestAt === undefined) return;
    const baseline = baselineRef.current;
    baselineRef.current = latestAt;
    if (baseline === undefined || latestAt === null) return;
    if (baseline !== null && latestAt <= baseline) return;
    // The dropdown list doesn't poll — refresh it so the new item is there.
    void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    if (!isNotificationSoundEnabled()) return;
    void claimNotificationChime(latestAt).then((won) => {
      if (won) playNotificationSound();
    });
  }, [latestAt, queryClient]);

  // Notifications are ephemeral — opening or ✕-ing one removes it for good.
  const dismiss = useMutation({
    mutationFn: (id: string) => notificationsService.dismiss(id),
    onSuccess: invalidate,
  });
  const markAll = useMutation({
    mutationFn: () => notificationsService.markAllRead(),
    onSuccess: invalidate,
  });

  const unread = unreadQuery.data?.count ?? 0;
  const items = listQuery.data?.data ?? [];

  // Items are plain buttons (not DropdownMenuItem), so Radix won't auto-close
  // the panel — close it here, otherwise it hangs over whatever the link opens.
  const onClickItem = (n: Notification) => {
    setOpen(false);
    dismiss.mutate(n.id);
    if (n.link) navigate(n.link);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-8 w-8 text-foreground/70 hover:text-foreground sm:h-9 sm:w-9" aria-label="Notifications">
          <Bell className="h-4 w-4 sm:h-[18px] sm:w-[18px]" />
          {unread > 0 && (
            <span className="absolute right-1 top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rose-500 px-1 text-[9px] font-bold text-white ring-2 ring-background">
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="flex max-h-[min(28rem,calc(100dvh-5rem))] w-[min(22rem,calc(100vw-1.5rem))] flex-col overflow-hidden p-0"
      >
        <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border/60 px-3 py-2.5">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold tracking-tight">Notifications</span>
            {unread > 0 && (
              <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
                {unread} new
              </span>
            )}
          </div>
          {unread > 0 && (
            <button
              type="button"
              onClick={() => markAll.mutate()}
              disabled={markAll.isPending}
              className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:opacity-50"
            >
              <CheckCheck className="h-3.5 w-3.5" /> Mark all read
            </button>
          )}
        </div>

        <div className="thin-scrollbar min-h-0 flex-1 overflow-y-auto overscroll-contain">
          {listQuery.isPending ? (
            <div className="space-y-1 p-2">
              {[0, 1, 2].map((i) => (
                <div key={i} className="flex gap-2.5 rounded-lg p-2">
                  <div className="h-8 w-8 shrink-0 animate-pulse rounded-full bg-muted" />
                  <div className="flex-1 space-y-1.5 py-0.5">
                    <div className="h-3 w-3/4 animate-pulse rounded bg-muted" />
                    <div className="h-2.5 w-1/2 animate-pulse rounded bg-muted" />
                  </div>
                </div>
              ))}
            </div>
          ) : listQuery.isError ? (
            <div className="px-4 py-10 text-center">
              <p className="text-sm text-muted-foreground">Couldn&apos;t load notifications</p>
              <button
                type="button"
                onClick={() => void listQuery.refetch()}
                className="mt-2 text-xs font-medium text-primary hover:underline"
              >
                Retry
              </button>
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
                <Bell className="h-4 w-4 text-muted-foreground" />
              </span>
              <p className="text-sm font-medium">You&apos;re all caught up</p>
              <p className="text-xs text-muted-foreground">New notifications will show up here.</p>
            </div>
          ) : (
            <ul className="p-1">
              {items.map((n) => {
                const { icon: Icon, className: iconClass } = styleForType(n.type);
                return (
                  <li key={n.id} className="group relative">
                    <button
                      type="button"
                      onClick={() => onClickItem(n)}
                      className={cn(
                        'flex w-full gap-2.5 rounded-lg p-2 text-left transition-colors hover:bg-accent',
                        !n.is_read && 'bg-primary/[0.06]',
                      )}
                    >
                      <span className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-full', iconClass)}>
                        <Icon className="h-4 w-4" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-start gap-1.5">
                          <span className={cn('line-clamp-2 flex-1 text-[13px] leading-snug', n.is_read ? 'font-medium text-foreground/80' : 'font-semibold')}>
                            {n.title || 'Notification'}
                          </span>
                          {!n.is_read && <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />}
                        </span>
                        {n.body && (
                          <span className="mt-0.5 line-clamp-1 block text-xs text-muted-foreground">{n.body}</span>
                        )}
                        <span className="mt-0.5 block text-[10.5px] text-muted-foreground/80">
                          {formatRelativeTime(n.created_at)}
                        </span>
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => dismiss.mutate(n.id)}
                      aria-label="Dismiss notification"
                      title="Dismiss"
                      className="absolute right-1.5 top-1.5 hidden h-6 w-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-background hover:text-foreground group-hover:flex"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="shrink-0 border-t border-border/60 px-3 py-2 text-center">
          <Link
            to="/notifications"
            onClick={() => setOpen(false)}
            className="text-xs font-medium text-primary hover:underline"
          >
            View all notifications
          </Link>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
